import React from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Navigation, MessageCircle, Users, Globe } from "lucide-react";
import { useT } from "../i18n/LanguageContext";

const MAPS_URL = "https://www.google.com/maps/dir/?api=1&destination=41.758925%2C12.266887&destination_name=Provalo+Spiedinificio";
const MAPS_EMBED = "https://www.google.com/maps?q=41.758925,12.266887&z=16&output=embed";
const ADDRESS = "Piazzale E. Molinari (ex Piazzale Mediterraneo), 00054 Fiumicino — Isola Sacra (RM)";
const PHONE_PRIMARY = "06 6502 4213";
const PHONE_MOBILE = "334 704 9226";
const WHATSAPP_NUMBER = "393347049226";
const EMAIL = "info@provalo.eu";

const waMessages = {
  it: "Ciao, vorrei prenotare un tavolo per giovedì.",
  en: "Hello, I'd like to book a table for Thursday.",
  es: "Hola, me gustaría reservar una mesa para el jueves.",
  fr: "Bonjour, je voudrais réserver une table pour jeudi.",
  de: "Hallo, ich möchte einen Tisch für Donnerstag reservieren.",
};

export const Navigatore = () => {
  const { t, lang } = useT();
  const x = t.navigatore;
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(waMessages[lang] || waMessages.it)}`;
  return (
    <section className="relative section-pad section-pad-top-tight section-pad-bottom-tight overflow-hidden bg-noir" data-testid="navigatore-section">
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="relative max-w-[1200px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <motion.a href={MAPS_URL} target="_blank" rel="noopener noreferrer"
            initial={{ opacity: 0, scale: 1.03 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
            transition={{ duration: 1.2, ease: [0.22, 0.61, 0.36, 1] }}
            className="lg:col-span-7 group relative block frame aspect-[16/10] cursor-pointer"
            data-testid="map-preview-link"
          >
            <iframe title="Provalo Spiedinificio — Mappa"
              className="absolute inset-0 w-full h-full grayscale-[40%] contrast-[1.1] brightness-[0.7]"
              loading="lazy" src={MAPS_EMBED} style={{ border: 0 }}
            />
            <div className="absolute inset-0 pointer-events-none transition-opacity duration-500 group-hover:opacity-60"
              style={{ background: "linear-gradient(135deg, rgba(14,10,7,0.55) 0%, rgba(212,97,42,0.18) 100%)" }}
            />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full pointer-events-none flex flex-col items-center">
              <div className="px-4 py-2 rounded-full bg-[var(--pn-amber)] text-[var(--pn-black)] text-[12px] font-bold tracking-wider uppercase shadow-lg">
                {x.pin}
              </div>
              <div className="w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[10px] border-t-[var(--pn-amber)]" />
            </div>
            <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-md border border-white/15 px-3.5 py-2 rounded-full text-[12px] text-[var(--pn-milk)] tracking-wide group-hover:border-[var(--pn-amber)]/60 transition-colors">
              {x.mapOpen}
            </div>
          </motion.a>

          <div className="lg:col-span-5">
            <motion.span
              initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
              transition={{ duration: 0.8 }} className="eyebrow"
            >
              {x.eyebrow}
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.22, 0.61, 0.36, 1] }}
              className="mt-5 font-display font-light text-[34px] sm:text-[44px] md:text-[56px] leading-[1.0] tracking-[-0.012em] text-[var(--pn-milk)]"
            >
              {x.title_a}<em className="italic text-[var(--pn-amber)]">{x.title_b}</em>{x.title_c}
            </motion.h2>

            <motion.div
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.9 }} className="mt-10 space-y-5"
            >
              <div className="flex items-start gap-4">
                <MapPin size={18} className="text-[var(--pn-amber)] mt-1 flex-shrink-0" strokeWidth={1.5} />
                <p className="text-[15px] md:text-[16px] text-[var(--pn-cream)]/85 leading-relaxed font-light">{ADDRESS}</p>
              </div>
              <div className="flex items-start gap-4">
                <Phone size={18} className="text-[var(--pn-amber)] flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div className="flex flex-col gap-1.5">
                  <a href={`tel:+39${PHONE_PRIMARY.replace(/\s/g, "")}`}
                    className="text-[15px] md:text-[16px] text-[var(--pn-cream)]/85 hover:text-[var(--pn-amber)] transition-colors font-light tracking-wider"
                    data-testid="phone-primary"
                  >
                    {PHONE_PRIMARY}
                  </a>
                  <a href={`tel:+39${PHONE_MOBILE.replace(/\s/g, "")}`}
                    className="text-[15px] md:text-[16px] text-[var(--pn-cream)]/85 hover:text-[var(--pn-amber)] transition-colors font-light tracking-wider"
                    data-testid="phone-mobile"
                  >
                    {PHONE_MOBILE}
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Mail size={18} className="text-[var(--pn-amber)] flex-shrink-0" strokeWidth={1.5} />
                <a href={`mailto:${EMAIL}`} className="text-[15px] md:text-[16px] text-[var(--pn-cream)]/85 hover:text-[var(--pn-amber)] transition-colors font-light">{EMAIL}</a>
              </div>
              <div className="flex items-center gap-4">
                <Globe size={18} className="text-[var(--pn-amber)] flex-shrink-0" strokeWidth={1.5} />
                <a href="https://provalo.eu" target="_blank" rel="noopener noreferrer"
                  className="text-[15px] md:text-[16px] text-[var(--pn-cream)]/85 hover:text-[var(--pn-amber)] transition-colors font-light"
                  data-testid="link-provalo-eu"
                >
                  provalo.eu
                </a>
              </div>
              <div className="flex items-start gap-4">
                <Clock size={18} className="text-[var(--pn-amber)] mt-1 flex-shrink-0" strokeWidth={1.5} />
                <div className="text-[14px] md:text-[15px] text-[var(--pn-cream)]/70 leading-relaxed font-light">
                  {x.hoursTitle} <span className="text-[var(--pn-cream)]/95">{x.hours1}</span>
                  {x.hoursWE && (
                    <>
                      <br />
                      {x.hoursWE} <span className="text-[var(--pn-cream)]/95">{x.hours2}</span>
                    </>
                  )}
                  {x.monClosed && (
                    <>
                      <br />
                      <span className="text-[var(--pn-cream)]/45">{x.monClosed}</span>
                    </>
                  )}
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.9 }}
              className="mt-10 flex flex-col sm:flex-row gap-3 items-start"
            >
              <a href={MAPS_URL} target="_blank" rel="noopener noreferrer"
                className="cta-map" data-testid="cta-navigator"
              >
                <Navigation size={18} strokeWidth={1.8} className="text-[var(--pn-amber)]" />
                <span>{x.ctaNav}</span>
                <span aria-hidden className="text-[var(--pn-amber)]">→</span>
              </a>
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer"
                className="cta-whatsapp" data-testid="cta-whatsapp"
              >
                <MessageCircle size={17} strokeWidth={2} />
                <span>{x.ctaWA}</span>
              </a>
            </motion.div>
            <p className="mt-3 text-[12px] text-[var(--pn-cream)]/40 font-light">{x.ctaNavHint}</p>

            <motion.div
              initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: 0.55, duration: 0.9 }}
              className="mt-8 p-5 rounded-2xl border border-[var(--pn-amber)]/25 bg-[rgba(224,162,74,0.04)] flex items-start gap-4"
            >
              <Users size={20} className="text-[var(--pn-amber)] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              <div>
                <p className="font-display italic text-[16px] md:text-[17px] text-[var(--pn-milk)] leading-snug">
                  {x.groupTitle}
                </p>
                <p className="mt-2 text-[13.5px] md:text-[14px] text-[var(--pn-cream)]/65 leading-relaxed font-light">
                  {x.groupBody}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
